import { UndefinedInitialDataOptions, useMutation } from '@tanstack/react-query';

import { ContactQuestionParams } from '../../interfaces/Contact';
import { Customer } from '../../interfaces/Customer';
import { Ticket } from '../../interfaces/Ticket';
import TicketService from '../../services/TicketService';

export enum TICKET_QUERY_KEYS {
  CANCEL_TICKET = 'cancel-ticket',
  GET_TICKET = 'get-ticket',
  SAVE_NO_SLOT_INFORMATION = 'save-no-slot-information',
  SEND_CONTACT_QUESTION = 'send-contact-question',
  UPDATE_CUSTOMER = 'update-customer',
}

const ticketService = new TicketService();

export const getTicketQueryConfig = (
  hashId: string
): UndefinedInitialDataOptions<Ticket, Error, Ticket, (string | TICKET_QUERY_KEYS)[]> => ({
  queryKey: [TICKET_QUERY_KEYS.GET_TICKET, hashId],
  queryFn: () => ticketService.getTicket(hashId),
  enabled: !!hashId,
});

export const useCancelTicketMutation = () => {
  const { mutate, isPending, isSuccess, isError } = useMutation<void, Error, string>({
    mutationKey: [TICKET_QUERY_KEYS.CANCEL_TICKET],
    mutationFn: (appointmentId: string) => ticketService.cancelTicket(appointmentId),
  });

  return {
    cancelTicket: mutate,
    isPending,
    isSuccess,
    isError,
  };
};

export const useSendContactQuestionMutation = (hashId: string) => {
  const { mutate, isPending, isSuccess, isError, reset } = useMutation<void, Error, ContactQuestionParams>({
    mutationKey: [TICKET_QUERY_KEYS.SEND_CONTACT_QUESTION],
    mutationFn: (params) => ticketService.sendContactQuestion(hashId, params),
  });

  return {
    sendContactQuestion: mutate,
    reset,
    isPending,
    isSuccess,
    isError,
  };
};

export const useUpdateCustomerMutation = () => {
  const { mutateAsync, isPending, isError, isSuccess } = useMutation<Customer, Error, { hashId: string } & Customer>({
    mutationKey: [TICKET_QUERY_KEYS.UPDATE_CUSTOMER],
    mutationFn: ({ hashId, ...customer }) => ticketService.updateCustomer(hashId, customer),
  });

  return {
    updateCustomer: mutateAsync,
    isPending,
    isError,
    isSuccess,
  };
};
